/* ============================================
   Native Possum — np-utilities.js
   Shared globals: npFormatMoney, npCeilToFive, npEscapeHtml,
   npIsSearchPage, npSwitchCurrency, npLayer, npScrollLock, npMotion, npMessage;
   plus the touch-device class, --np-vh and the offline banner.
   Load order: sync (no defer) in theme.liquid, after np-currency-map
   (window.NP_CURRENCY) and before np-header.js / other NP scripts.
   npSwitchCurrency: form.submit() is the only thing that changes the
   market server-side — never remove it.
   ============================================ */

(function() {
  'use strict';

  // ============================================
  // CURRENCY FALLBACK
  // Used only if window.NP_CURRENCY is missing — i.e. the
  // np-currency-map render in theme.liquid didn't run. Keep in
  // sync with np-currency-map.liquid, but that snippet is the
  // source of truth: add new currencies THERE, not here.
  // ============================================
  var NP_CURRENCY_FALLBACK = {
    'NZD': { symbol: '$', prefix: ''    },
    'AUD': { symbol: '$', prefix: 'AUD' },
    'USD': { symbol: '$', prefix: 'USD' },
    'CAD': { symbol: '$', prefix: 'CAD' },
    'GBP': { symbol: '£', prefix: ''    },
    'EUR': { symbol: '€', prefix: ''    }
  };

  // Thousands separators, matching Liquid's money filter output.
  function npGroupThousands(str) {
    var parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  // ============================================
  // CURRENCY FORMATTING
  // Returns prefix + symbol + amount, e.g. CAD$299 or $298.80.
  // Matches the Liquid money_without_trailing_zeros output.
  // Used by: np-bag-drawer, product scripts
  // ============================================
  window.npFormatMoney = function(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents.replace(/[^0-9]/g, ''), 10);
    }
    if (isNaN(cents)) cents = 0;

    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : 'NZD';

    var map = window.NP_CURRENCY || NP_CURRENCY_FALLBACK;
    var entry = map[currency] || { symbol: '$', prefix: '' };

    var amount = cents / 100;
    var formatted = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);

    return entry.prefix + entry.symbol + npGroupThousands(formatted);
  };

  // ============================================
  // ROUND UP TO THE NEXT 5 (in cents)
  // For "$X away from free shipping" messages. Always rounds UP so
  // the customer is never told they are closer than they are.
  // 1150 -> 1500, 1500 -> 1500, 1 -> 500.
  // Used by: np-bag-drawer.liquid, main-cart.liquid
  // ============================================
  window.npCeilToFive = function(cents) {
    var dollars = Math.ceil(cents / 100);
    return Math.max(5, Math.ceil(dollars / 5) * 5) * 100;
  };

  // ============================================
  // COLLAPSIBLE PANELS (disclosure)
  // Any trigger with data-np-disclosure + aria-controls keeps its panel
  // `inert` whenever aria-expanded isn't "true". Collapsed content (hidden by
  // max-height for the animation) is then skipped by Tab and screen readers.
  // Only for click-to-open panels — never for hover menus (inert blocks clicks).
  // Used by: main-product (size/care guides), collection (mobile filter
  //          accordions), np-mobile-menu (menu accordions)
  // ============================================
  function npSyncDisclosure(trigger) {
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (panel) panel.inert = trigger.getAttribute('aria-expanded') !== 'true';
  }
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-np-disclosure][aria-controls]').forEach(function(trigger) {
      npSyncDisclosure(trigger);
      new MutationObserver(function() { npSyncDisclosure(trigger); })
        .observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
    });
  });

  // ============================================
  // LAYERS (drawers, sheets, lightboxes, popups)
  // One helper so every popup behaves the same for keyboard and
  // screen-reader users. Visuals and animation stay with each
  // component — this handles:
  //   - everything outside the layer (and its `keep` areas) goes inert
  //   - its `backdrop` (the dimmed page behind it) stays live, and a click
  //     on it asks the top layer to close — components never wire their own
  //     backdrop/scrim/overlay clicks (check.js blocks it)
  //   - focus moves in on open and back to the trigger on close
  //   - ONE Escape handler: closes the top layer only
  //   - aria-expanded on the trigger
  //   - the panel itself is `inert` while closed (so an off-screen drawer
  //     can't be tabbed into) — give closed panels the `inert` attribute
  //     in their markup so this holds before any JS runs
  //   - groups: opening a layer closes any open layer in the same group
  //     first (the header's search, the menu and the bag are group
  //     'header' — only one at a time, no timers)
  //   - scroll lock (lockScroll), counted, so two layers never fight
  //   - a document event 'np:layer' {panel, open} after every open/close
  //     (np-header.js uses it for the header's open states)
  //   - too short to use (desktop browser zoomed in, mouse / trackpad only):
  //     when a scroll area inside the panel is squeezed under 120px, or the
  //     panel is taller than the screen, the panel gets .np-layer-short and scrolls
  //     as one — pinned headers / footers and inner scroll areas let go
  //     (np-base.css). Rechecked on resize, and when its content changes,
  //     while open.
  // Layers stack (e.g. currency drawer over the mobile menu).
  // A control whose aria-controls names the open panel is that panel's
  // toggle: using it never counts as "using a keep area".
  //
  //   npLayer.open(panel, {
  //     trigger,            // element that opened it (focus returns here)
  //     keep: [els],        // areas that stay usable (e.g. header for the bag)
  //     backdrop,           // optional: the dim layer behind; click = close
  //     closeOnKeepUse,     // true: using anything in `keep` (other than
  //                         //   the trigger or a toggle) asks the layer to close
  //     initialFocus,       // element or selector; default first focusable
  //     group,              // optional: see above
  //     lockScroll,         // optional: true, or { unlockDelay: ms }
  //     onRequestClose      // REQUIRED: the component's own close()
  //   });
  //   npLayer.close(panel);   // call from the component's close()
  //   npLayer.isOpen(panel);
  //
  // SCROLL LOCK (npScrollLock.lock() / .unlock(delayMs)) — also usable on
  // its own. Phones (≤767): body position:fixed at the scroll offset (iOS
  // ignores overflow:hidden), restored on unlock. Wider: overflow hidden.
  // ============================================
  (function() {
    var count = 0, mode = null, savedY = 0;
    window.npScrollLock = {
      lock: function() {
        if (count++ > 0) return;
        if (window.innerWidth <= 767) {
          mode = 'fixed';
          savedY = window.scrollY;
          document.body.classList.add('np-scroll-locked');
          document.body.style.top = '-' + savedY + 'px';
        } else {
          mode = 'overflow';
          document.body.style.overflow = 'hidden';
        }
      },
      unlock: function(delayMs) {
        if (count === 0) return;
        if (delayMs) { setTimeout(function() { window.npScrollLock.unlock(); }, delayMs); return; }
        if (--count > 0) return;
        if (mode === 'fixed') {
          document.body.classList.remove('np-scroll-locked');
          document.body.style.top = '';
          window.scrollTo(0, savedY);
        } else if (mode === 'overflow') {
          document.body.style.overflow = '';
        }
        mode = null;
      },
      isLocked: function() { return count > 0; }
    };
  })();

  (function() {
    var stack = [];
    var applied = [];           // elements this helper made inert
    var SKIP = { SCRIPT: 1, STYLE: 1, LINK: 1, TEMPLATE: 1, NOSCRIPT: 1 };
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
      'select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

    function release() {
      applied.forEach(function(el) { el.inert = false; });
      applied = [];
    }

    function applyTop() {
      release();
      var top = stack[stack.length - 1];
      if (!top) return;
      // Walk down from <body>: anything that is (or is inside) a kept element
      // stays live; ancestors of kept elements are walked into; everything
      // else is made inert. Kept elements' own contents are never touched.
      var keep = new Set([top.panel].concat(top.keep, top.backdrop ? [top.backdrop] : []));
      var ancestors = new Set();
      keep.forEach(function(el) {
        for (var n = el.parentElement; n && n !== document.documentElement; n = n.parentElement) ancestors.add(n);
      });
      (function walk(node) {
        Array.prototype.forEach.call(node.children, function(child) {
          if (keep.has(child) || SKIP[child.tagName]) return;
          if (ancestors.has(child)) { walk(child); return; }
          if (child.inert) return;
          child.inert = true;
          applied.push(child);
        });
      })(document.body);
    }

    function focusInto(layer) {
      var target = layer.initialFocus;
      if (typeof target === 'string') target = layer.panel.querySelector(target);
      if (!target) {
        target = Array.prototype.find.call(layer.panel.querySelectorAll(FOCUSABLE), function(el) {
          return el.getClientRects().length > 0;
        });
      }
      if (!target) {
        if (!layer.panel.hasAttribute('tabindex')) layer.panel.setAttribute('tabindex', '-1');
        target = layer.panel;
      }
      target.focus({ preventScroll: true });
    }

    function isToggleFor(panel, target) {
      var c = target && target.closest ? target.closest('[aria-controls]') : null;
      return !!(c && panel.id && c.getAttribute('aria-controls') === panel.id);
    }

    function onKeepUse(e) {
      var top = stack[stack.length - 1];
      if (!top || !top.closeOnKeepUse) return;
      if (top.panel.contains(e.target)) return;
      if (top.trigger && top.trigger.contains(e.target)) return;
      if (isToggleFor(top.panel, e.target)) return;
      if (top.keep.some(function(k) { return k.contains(e.target); })) top.onRequestClose();
    }

    // Too short to use: see the header notes. Inner scroll areas and pinned
    // (sticky) parts are marked data-np-fit so np-base.css can let them go —
    // an attribute, not a class, because components rewrite their className
    // when they re-render (the bag footer does).
    var fineMQ = window.matchMedia('(pointer: fine)');
    var MIN_SCROLL_H = 120;

    function fit(panel) {
      panel.classList.remove('np-layer-short');
      Array.prototype.forEach.call(panel.querySelectorAll('[data-np-fit]'), function(el) {
        el.removeAttribute('data-np-fit');
      });
      if (!fineMQ.matches || !panel.getClientRects().length) return;
      // offsetHeight, not the on-screen box: sheets are still sliding in
      var squeezed = panel.offsetHeight > window.innerHeight + 1;
      Array.prototype.forEach.call(panel.querySelectorAll('*'), function(el) {
        var cs = getComputedStyle(el);
        if (cs.position === 'sticky') el.setAttribute('data-np-fit', 'pin');
        // Vertical scroll areas with more than they show (a sideways
        // scroller's height already fits, so it's left alone)
        if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.getClientRects().length &&
            el.scrollHeight > el.clientHeight + 1) {
          el.setAttribute('data-np-fit', 'scroll');
          if (el.clientHeight < MIN_SCROLL_H) squeezed = true;
        }
      });
      panel.classList.toggle('np-layer-short', squeezed);
    }

    window.addEventListener('resize', function() {
      stack.forEach(function(l) { fit(l.panel); });
    });

    // Content can arrive after opening (the bag re-syncs the cart as it
    // opens), so refit when what's inside changes. childList only: fit's own
    // class changes don't retrigger it.
    function watchFit(layer) {
      if (!fineMQ.matches || !window.MutationObserver) return;
      var queued = false;
      layer.fitObserver = new MutationObserver(function() {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function() { queued = false; fit(layer.panel); });
      });
      layer.fitObserver.observe(layer.panel, { childList: true, subtree: true, characterData: true });
    }

    function announce(panel, open) {
      document.dispatchEvent(new CustomEvent('np:layer', { detail: { panel: panel, open: open } }));
    }

    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape' || !stack.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      stack[stack.length - 1].onRequestClose();
    }, true);
    // A click on the top layer's backdrop closes that layer (and only it, so
    // a drawer stacked on another closes alone).
    document.addEventListener('click', function(e) {
      var top = stack[stack.length - 1];
      if (!top || !top.backdrop || !top.backdrop.contains(e.target)) return;
      e.preventDefault();
      top.onRequestClose();
    });
    document.addEventListener('pointerdown', onKeepUse, true);
    document.addEventListener('focusin', onKeepUse, true);

    window.npLayer = {
      open: function(panel, opts) {
        if (!panel) return;
        opts = opts || {};
        if (this.isOpen(panel)) return;
        if (opts.group) {
          stack.filter(function(l) { return l.group === opts.group; })
            .forEach(function(l) { l.onRequestClose(); });
        }
        var layer = {
          panel: panel,
          trigger: opts.trigger || (document.activeElement !== document.body ? document.activeElement : null),
          keep: (opts.keep || []).filter(Boolean),
          backdrop: opts.backdrop || null,
          closeOnKeepUse: !!opts.closeOnKeepUse,
          initialFocus: opts.initialFocus,
          onRequestClose: opts.onRequestClose || function() { window.npLayer.close(panel); },
          group: opts.group || null,
          lockScroll: opts.lockScroll || null
        };
        panel.inert = false;
        stack.push(layer);
        if (layer.lockScroll) window.npScrollLock.lock();
        if (layer.trigger && layer.trigger.hasAttribute('aria-expanded')) layer.trigger.setAttribute('aria-expanded', 'true');
        applyTop();
        // Wait a frame so the component's own "show" styles have applied.
        requestAnimationFrame(function() { fit(panel); watchFit(layer); focusInto(layer); });
        announce(panel, true);
      },
      close: function(panel) {
        var i = -1;
        for (var k = stack.length - 1; k >= 0; k--) { if (stack[k].panel === panel) { i = k; break; } }
        if (i === -1) return;
        var layer = stack.splice(i, 1)[0];
        panel.inert = true;
        if (layer.fitObserver) layer.fitObserver.disconnect();
        panel.classList.remove('np-layer-short');
        applyTop();
        var t = layer.trigger;
        if (t && t.hasAttribute('aria-expanded')) t.setAttribute('aria-expanded', 'false');
        var active = document.activeElement;
        var focusWasInside = !active || active === document.body || panel.contains(active);
        if (focusWasInside && t && document.contains(t) && t.getClientRects().length) {
          t.focus({ preventScroll: true });
        }
        if (layer.lockScroll) window.npScrollLock.unlock(layer.lockScroll.unlockDelay || 0);
        announce(panel, false);
      },
      isOpen: function(panel) {
        return stack.some(function(l) { return l.panel === panel; });
      }
    };
  })();

  // ============================================
  // MOTION (WCAG 2.3.3): the ONE place scripts ask whether to animate.
  //   npMotion.reduced()   true when the shopper asked for reduced motion
  //   npMotion.scroll()    'smooth', or 'auto' when reduced — use it for every
  //                        scrollTo / scrollIntoView behavior (check.js blocks
  //                        a typed 'smooth' anywhere else)
  // CSS animations/transitions are already cut by np-base.css's reduced-motion rule.
  // ============================================
  (function() {
    var q = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    window.npMotion = {
      reduced: function() { return !!(q && q.matches); },
      scroll: function() { return q && q.matches ? 'auto' : 'smooth'; }
    };
  })();

  // ============================================
  // MESSAGES THAT GO AWAY (WCAG 2.2.1, 2.2.2): the ONE place a message
  // decides when to leave. No message is hidden on a bare setTimeout.
  //   npMessage.dwell(el, hide)   a confirmation ("Removed from bag",
  //       "Link copied", a hint): stays for a reading time (4s, a little
  //       longer for longer text, never over 10s) and pauses while a mouse
  //       is over it or focus is inside it. Touch never pauses it (a tap
  //       can leave a phone "hovering" and the message would never leave).
  //   npMessage.hold(el, hide)    an error: stays until the shopper acts —
  //       their next click / tap or change anywhere (choosing a size,
  //       trying again). Tab and arrow keys don't count, so a keyboard or
  //       screen-reader user can move to it and read it.
  //   Either kind: a click / tap on the message, or Escape, clears it.
  // Showing a new message on the same element cancels the old timing.
  // `hide` is the component's own way to hide it (remove(), a class...).
  // ============================================
  (function() {
    function cancel(el) {
      if (el && el._npMessageStop) { el._npMessageStop(); el._npMessageStop = null; }
    }

    // A click / tap on the message, or Escape, clears it. Returns the undo.
    function dismissable(el, clear) {
      function onClick() { clear(); }
      function onKey(e) { if (e.key === 'Escape') clear(); }
      el.addEventListener('click', onClick);
      document.addEventListener('keydown', onKey);
      return function() {
        el.removeEventListener('click', onClick);
        document.removeEventListener('keydown', onKey);
      };
    }

    function readingTime(el) {
      var words = ((el && el.textContent) || '').trim().split(/\s+/).length;
      return Math.min(10000, Math.max(4000, 1000 + words * 300));
    }

    function dwell(el, hide) {
      if (!el) return;
      cancel(el);
      var timer = null;
      var mouseOver = false;
      function start() { clearTimeout(timer); timer = setTimeout(done, readingTime(el)); }
      function enter(e) { if (e.pointerType === 'mouse') { mouseOver = true; clearTimeout(timer); } }
      function leave(e) { if (e.pointerType === 'mouse') { mouseOver = false; resume(); } }
      function focusIn() { clearTimeout(timer); }
      function resume() {
        if (mouseOver || el.contains(document.activeElement)) return;
        start();
      }
      var dismiss = dismissable(el, function() { done(); });
      function stop() {
        clearTimeout(timer);
        dismiss();
        el.removeEventListener('pointerenter', enter);
        el.removeEventListener('pointerleave', leave);
        el.removeEventListener('focusin', focusIn);
        el.removeEventListener('focusout', resume);
      }
      function done() { stop(); el._npMessageStop = null; hide(); }
      el.addEventListener('pointerenter', enter);
      el.addEventListener('pointerleave', leave);
      el.addEventListener('focusin', focusIn);
      el.addEventListener('focusout', resume);
      el._npMessageStop = stop;
      start();
    }

    function hold(el, hide) {
      if (!el) return;
      cancel(el);
      var armed = false;
      function act(e) {
        if (!armed) return;
        done();
      }
      var dismiss = dismissable(el, function() { done(); });
      function stop() {
        dismiss();
        document.removeEventListener('click', act, true);
        document.removeEventListener('change', act, true);
      }
      function done() { stop(); el._npMessageStop = null; hide(); }
      // Armed after this turn, so the click that raised the error doesn't clear it
      setTimeout(function() { armed = true; }, 0);
      document.addEventListener('click', act, true);
      document.addEventListener('change', act, true);
      el._npMessageStop = stop;
    }

    window.npMessage = { dwell: dwell, hold: hold, cancel: cancel };
  })();

  // ============================================
  // HTML ESCAPING
  // Prevents XSS in dynamically inserted content.
  // Used by: np-predictive.js and other NP scripts
  // ============================================
  window.npEscapeHtml = function(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // ============================================
  // SEARCH RESULTS PAGE?
  // Search runs on the NP Browse app at /apps/browse?q=… (/search
  // forwards there). The header search boxes use this to keep the
  // shopper's words in the box on the results page.
  // Used by: np-header.js
  // ============================================
  window.npIsSearchPage = function() {
    var path = window.location.pathname;
    return path === '/search' ||
      (path.indexOf('/apps/browse') === 0 && new URLSearchParams(window.location.search).has('q'));
  };

  // ============================================
  // CURRENCY SWITCHING
  // Submits the Shopify localization form. That form POST is the
  // ONLY thing that changes the market server-side — never remove
  // it. A previous v6 did, and currency switching broke entirely
  // (NZD switched straight back to CAD).
  //
  // Called with a null form by np-region-popup's NZD dismiss path,
  // in which case this is currently a no-op.
  //
  // Used by: np-header.js, np-bag-drawer, np-mobile-menu, np-region-popup
  // ============================================
  window.npSwitchCurrency = function(countryCode, form) {
    if (form) form.submit();
  };

  // ============================================
  // PAGE-WIDE SETUP (runs once, from <body>)
  // - html.np-touch-device on touch screens (np-product.css lightbox zoom)
  // - --np-vh: the visible viewport height at load (stops iOS address-bar
  //   layout shift; np-product-mobile.js updates it on product pages)
  // - the offline banner (styles: np-mobile.css)
  // ============================================
  if (('ontouchstart' in window) || navigator.maxTouchPoints > 0) {
    document.documentElement.classList.add('np-touch-device');
  }
  document.documentElement.style.setProperty('--np-vh', window.innerHeight + 'px');

  (function() {
    var banner = document.createElement('div');
    banner.className = 'np-offline-banner';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'assertive');
    banner.textContent = 'No internet connection';
    function place() { if (document.body && !banner.parentNode) document.body.appendChild(banner); }
    function show() { place(); banner.classList.add('active'); }
    function hide() { banner.classList.remove('active'); }
    window.addEventListener('offline', show);
    window.addEventListener('online', hide);
    if (document.body) place(); else document.addEventListener('DOMContentLoaded', place);
    if (!navigator.onLine) show();
  })();

})();